<template>
  <div>
    <grand-summary></grand-summary>
    <b-container class="mt-2" data-test="market-comparison">
      <vue-headful :title="this.setPageTitle('marketComparation', decodedProductName)" />
      <loading :active="globalLoading" :color="loaderColor" is-full-page loader="spinner"> </loading>
      <b-row class="mb-3" no-gutters>
        <b-col class="productInfo" cols="12"
          ><span class="detailsTemplateProductName mr-2"
            ><a
              @click.prevent="
                $router.push({
                  name: 'productDetails',
                  params: { productName: productName, issuer: issuer, referenceDate: referenceDate },
                })
              "
              class="pointer"
              ><span class="small mr-2 align-baseline"><i class="fas fa-chevron-left"></i></span></a></span
          ><span>
            <span class="detailsTemplateProductName font-weight-bold">{{ decodedProductName }}</span
            ><br />
            <div v-if="investmentOptionName !== null">
              <span class="detailsTemplateOptionName">{{ decodedOptionName }}</span
              ><br />
            </div>
            <span class="detailsTemplateIssuer">
              {{ issuerName }}
            </span>
          </span></b-col
        >
      </b-row>
      <!-- Catalog-native filter panel — same filters-catalog groups as the search page,
           local component state instead of the shared search Vuex module (this page isn't a
           full search page). Every change re-runs the comparison immediately (no separate
           Apply button). -->
      <b-row class="mb-2" no-gutters v-if="smartFilterEntry">
        <b-col cols="6">
          <label class="fieldLabel" for="marketComparisonSmartFilter" v-t="'search.label.productName'"></label>
          <autocomplete-product :value="smartFilterValue" skip-input-when-actionable @input="onSmartFilterChange">
          </autocomplete-product>
        </b-col>
      </b-row>
      <b-row class="mb-2" no-gutters v-if="activeFilters.length > 0">
        <b-col cols="12">
          <cancellable-badge
            v-for="chip in filterChips"
            :key="chip.id"
            class="active-filter-chip"
            :text="chip.text"
            variant="secondary"
            @cancel="onChipCancel(chip)"
          ></cancellable-badge>
        </b-col>
      </b-row>
      <b-row class="mb-3" no-gutters>
        <b-col cols="12">
          <div class="fsb-body">
            <filter-accordion-section
              v-for="(group, index) in filterGroups"
              :key="group.groupKey"
              :title="groupLabel(group.groupKey)"
              :start-open="false"
              :last="index === filterGroups.length - 1 && otherUngrouped.length === 0"
            >
              <div v-for="filter in group.filters" :key="filter.filterKey" class="fsb-field">
                <div>
                  <label class="fsb-label">{{ filterLabel(filter.filterKey) }}</label>
                  <div class="fsb-select-wrap">
                    <filter-field
                      :filter="filter"
                      :value="filterValue(filter.filterKey)"
                      @input="(value) => onFilterInput(filter.filterKey, value)"
                    ></filter-field>
                  </div>
                </div>
              </div>
            </filter-accordion-section>
            <filter-accordion-section
              v-if="otherUngrouped.length > 0"
              :title="$t('search.ungrouped')"
              :last="true"
            >
              <div v-for="filter in otherUngrouped" :key="filter.filterKey" class="fsb-field">
                <div>
                  <label class="fsb-label">{{ filterLabel(filter.filterKey) }}</label>
                  <div class="fsb-select-wrap">
                    <filter-field
                      :filter="filter"
                      :value="filterValue(filter.filterKey)"
                      @input="(value) => onFilterInput(filter.filterKey, value)"
                    ></filter-field>
                  </div>
                </div>
              </div>
            </filter-accordion-section>
          </div>
        </b-col>
      </b-row>
      <b-row class="mb-4">
        <b-col cols="12">
          <b-container v-if="investmentOptionName === null">
            <comparison-message
              :icon-name="getProductComparisonMessageIcon.icon"
              :icon-class="getProductComparisonMessageIcon.class"
              :message="
                $tc('search.label.comparison', marketProductsTotalCount) +
                (showSearchCompatibilityAlert
                  ? '<span class=' + 'alert-msg' + '>' + $t('marketComparation.missingProduct') + '</span>'
                  : '')
              "
            ></comparison-message>
          </b-container>
          <b-container v-else>
            <comparison-message
              :icon-name="getSidComparisonMessageIcon.icon"
              :icon-class="getSidComparisonMessageIcon.class"
              :message="
                $tc('search.label.comparisonOptions', marketInvestmentOptionsTotalCount) +
                ' ' +
                $tc('search.label.comparisonProducts', marketProductsTotalCount) +
                (showSearchCompatibilityAlert
                  ? '<span class=' + 'alert-msg' + '>' + $t('marketComparation.missingProduct') + '</span>'
                  : '')
              "
            ></comparison-message>
          </b-container>
        </b-col>
      </b-row>
      <b-row>
        <b-col v-if="indicesValues.riskInd.length > 0" :cols="setColumnsParent">
          <market-comp-chart
            chart-category="risk"
            indicator-type=""
            :data="BEResponses.risk"
            :header="$t('common.indicators.riskInd')"
            :indices-values="indicesValues.riskInd"
            :is-loading="isLoading.risk"
            :global-loading="globalLoading"
            :is-mop="isMop"
            :loader-color="loaderColor"
            @column-click="showProducts"
            v-model="indicators.riskInd"
            data-test="mcc-risk"
          ></market-comp-chart>
        </b-col>
        <b-col v-if="indicesValues.performanceInd.length > 0" :cols="setColumnsParent">
          <market-comp-chart
            chart-category="performance"
            :data="BEResponses.performance"
            :header="$t('common.indicators.performanceInd')"
            :indices-values="indicesValues.performanceInd"
            :is-loading="isLoading.performance"
            :global-loading="globalLoading"
            :is-mop="isMop"
            :loader-color="loaderColor"
            @column-click="showProducts"
            v-model="indicators.performanceInd"
          ></market-comp-chart>
        </b-col>
        <b-col v-if="indicesValues.returnInd.length > 0" :cols="setColumnsParent">
          <market-comp-chart
            chart-category="return"
            :data="BEResponses.return"
            :header="$t('common.indicators.returnInd')"
            :indices-values="indicesValues.returnInd"
            :is-loading="isLoading.return"
            :global-loading="globalLoading"
            :is-mop="isMop"
            :loader-color="loaderColor"
            @column-click="showProducts"
            v-model="indicators.returnInd"
          ></market-comp-chart>
        </b-col>
        <b-col v-if="indicesValues.costInd.length > 0" :cols="setColumnsParent">
          <market-comp-chart
            chart-category="cost"
            :data="BEResponses.cost"
            :header="$t('common.indicators.costInd')"
            :indices-values="indicesValues.costInd"
            :is-loading="isLoading.cost"
            :global-loading="globalLoading"
            :is-mop="isMop"
            :loader-color="loaderColor"
            @column-click="showProducts"
            v-model="indicators.costInd"
          ></market-comp-chart>
        </b-col>
        <b-col v-if="indicesValues.otherInd.length > 0" :cols="setColumnsParent">
          <market-comp-chart
            chart-category="other"
            :data="BEResponses.other"
            :header="$t('common.indicators.otherInd')"
            :indices-values="indicesValues.otherInd"
            :is-loading="isLoading.other"
            :global-loading="globalLoading"
            :is-mop="isMop"
            :loader-color="loaderColor"
            @column-click="showProducts"
            v-model="indicators.otherInd"
          ></market-comp-chart>
        </b-col>
        <custom-modal
          v-if="Object.keys(BEResponses).length > 0"
          :id="'marketComparation'"
          v-model="marketComparationModalValue"
          @on-click-table-row="onClickTableRow"
        ></custom-modal>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import MarketCompChart from '../components/MarketCompCharts/MarketCompChart'
import GrandSummary from '../components/Search/GrandSummary'
import ComparisonMessage from '../components/Lib/ComparisonMessage'
import CustomModal from '../components/Lib/CustomModal'
import FilterField from '../components/Search/FilterField'
import FilterAccordionSection from '../components/Search/FilterAccordionSection'
import AutocompleteProduct from '../components/Search/SearchFilters/AutocompleteProduct'
import CancellableBadge from '../components/Lib/CancellableBadge'
import { isValidDate } from '../utils/date'
import { rhpToYears, round } from '../utils/formatter'
import { base64UrlDecode, isBase64Url } from '../utils/sanitizer'
import { assetClassStorageKey } from '../utils/assetClassState'

export default {
  name: 'MarketComparison',

  components: {
    GrandSummary,
    MarketCompChart,
    ComparisonMessage,
    CustomModal,
    FilterField,
    AutocompleteProduct,
    CancellableBadge,
    FilterAccordionSection,
  },

  props: {
    productName: {
      type: String,
      default: '',
    },
    issuer: {
      type: String,
      default: '',
    },
    referenceDate: {
      type: String,
      default: '',
    },
    investmentOptionName: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      modalContent: {
        filter: '',
        valuesType: 'Min',
        isLastColumns: false,
        min: -Infinity,
        max: Infinity,
        chartCategory: 'risk',
      },
      isLoading: {
        risk: false,
        performance: false,
        cost: false,
        return: false,
        other: false,
      },
      globalLoading: true,
      // Catalog filter panel state — [{key, value}], same wire format the BE expects/returns.
      filterGroups: [],
      ungroupedFilters: [],
      activeFilters: [],
      // Indicator codes valid for the current asset class (GET /marketComparison/indicatorCodes),
      // read by `indicesValues` below to filter the per-category option lists (indices.riskInd/
      // performanceInd/returnInd/costInd/otherInd), which come from the locale files and are NOT
      // themselves asset-class-aware — e.g. InsuredEventIncrementRHP is IBIPs-only and must not
      // be selectable while viewing Investment Certificates.
      // `null` (not yet loaded, on the very first render before `mounted` resolves) means "don't
      // filter yet", so the page doesn't flash an empty dropdown before the fetch completes.
      validIndicatorCodes: null,
      indicators: {
        riskInd: 'MRM',
        performanceInd: 'SharpeRatioOnRiskFree',
        returnInd: 'StressReturn',
        costInd: 'EntryCosts',
        otherInd: 'KIDRHP',
      },
      BEResponses: {
        risk: { dataMin: [], dataMax: [], dataMed: [], productData: { min: 0, med: 0, max: 0 } },
        performance: { dataMin: [], dataMax: [], dataMed: [], productData: { min: 0, med: 0, max: 0 } },
        return: { dataMin: [], dataMax: [], dataMed: [], productData: { min: 0, med: 0, max: 0 } },
        cost: { dataMin: [], dataMax: [], dataMed: [], productData: { min: 0, med: 0, max: 0 } },
        other: { dataMin: [], dataMax: [], dataMed: [], productData: { min: 0, med: 0, max: 0 } },
      },
      comparedItem: 0,
      loaderColor: 'blue',
      marketProductsTotalCount: 0,
      marketInvestmentOptionsTotalCount: 0,
      showSearchCompatibilityAlert: false,
    }
  },
  watch: {
    indicators: {
      deep: true,
      handler(newVal) {
        localStorage.setItem(assetClassStorageKey('okv-market-comparation-indicators'), JSON.stringify(newVal))
      },
    },
    'indicators.riskInd': function (newValue) {
      if (this.$_restoring) return
      this.isLoading.risk = true
      this.search(this.getDataToSend([newValue]), (resp) => this._callbackOf('risk', resp))
    },
    'indicators.performanceInd': function (newValue) {
      if (this.$_restoring) return
      this.isLoading.performance = true
      this.search(this.getDataToSend([newValue]), (resp) => this._callbackOf('performance', resp))
    },
    'indicators.returnInd': function (newValue) {
      if (this.$_restoring) return
      this.isLoading.return = true
      this.search(this.getDataToSend([newValue]), (resp) => this._callbackOf('return', resp))
    },
    'indicators.costInd': function (newValue) {
      if (this.$_restoring) return
      this.isLoading.cost = true
      this.search(this.getDataToSend([newValue]), (resp) => this._callbackOf('cost', resp))
    },
    'indicators.otherInd': function (newValue) {
      if (this.$_restoring) return
      this.isLoading.other = true
      this.search(this.getDataToSend([newValue]), (resp) => this._callbackOf('other', resp))
    },
  },
  methods: {
    groupLabel(groupKey) {
      const key = 'search.groups.' + groupKey
      return this.$te(key) ? this.$t(key) : groupKey
    },
    filterLabel(filterKey) {
      const key = 'search.filters.' + filterKey
      return this.$te(key) ? this.$t(key) : filterKey
    },
    filterValue(filterKey) {
      const entry = this.activeFilters.find((f) => f.key === filterKey)
      return entry ? entry.value : null
    },
    onFilterInput(filterKey, value) {
      const others = this.activeFilters.filter((f) => f.key !== filterKey)
      const isEmptyRange =
        value !== null &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        Object.values(value).every((v) => v === null || v === undefined)
      const isEmpty = value === null || value === undefined || (Array.isArray(value) && value.length === 0) || isEmptyRange
      this.activeFilters = isEmpty ? others : [...others, { key: filterKey, value }]
      this.rerunSearch()
    },
    // SmartFilter widget — same guard as FiltersPanel.vue's onSmartFilterChange: the underlying
    // CustomAutocompGroupedSelect re-emits 'input' with the current text on every click anywhere on
    // the page (v-closable), so without this guard every click would re-trigger a search.
    onSmartFilterChange(value) {
      if ((value || '') === this.smartFilterValue) {
        return
      }
      this.onFilterInput('SmartFilter', value || null)
    },
    valueLabel(key, value) {
      if (typeof value === 'boolean') {
        return this.$t(value ? 'search.booleanTrue' : 'search.booleanFalse')
      }
      const optionKey = 'search.options.' + key + '.' + value
      return this.$te(optionKey) ? this.$t(optionKey) : value
    },
    onChipCancel(chip) {
      if (chip.item === null) {
        this.onFilterInput(chip.key, null)
        return
      }
      const entry = this.activeFilters.find((f) => f.key === chip.key)
      const remaining = (entry ? entry.value : []).filter((v) => v !== chip.item)
      this.onFilterInput(chip.key, remaining)
    },
    rerunSearch() {
      Object.keys(this.isLoading).forEach((key) => (this.isLoading[key] = true))
      const callbackCollection = (resp) => {
        this._callbackOf('risk', resp)
        this._callbackOf('performance', resp)
        this._callbackOf('cost', resp)
        this._callbackOf('return', resp)
        this._callbackOf('other', resp)
        this._isProductIncludedCallback(resp)
      }
      this.search(this.getDataToSend([...Object.values(this.indicators)]), callbackCollection)
    },
    getDataToSend(args) {
      return {
        productSearchFilters: this.activeFilters,
        productKey: { issuerCode: this.issuer, name: this.decodedProductName, referenceDate: this.referenceDate },
        indicators: args,
        investmentOptionName: this.decodedOptionName,
      }
    },
    search(dataToSend, callback) {
      this.post('/api/charts/marketComparison', dataToSend)
        .then((resp) => {
          if (resp != null) {
            resp.data = resp.data || []
            this.comparedItem = resp.paging.totalItems
            callback(resp)
          } else {
            alert(this.$t('common.errorReceivingData'))
          }
        })
        .catch((e) => {
          this.httpErrorsHandler(e)
        })
        .finally(() => {
          this.globalLoading = false
        })
    },
    _callbackOf(chart, resp) {
      const data = resp.data.find((el) => el.indicatorCode === this.indicators[chart + 'Ind'])
      if (data != null) {
        data.productChartItem = data.productChartItem || []
        this.BEResponses[chart] = {
          productData: data.productChartItem,
          dataMin: data.marketProductsChartItems.map((prod) => ({
            y: prod.min,
            meta: {
              ...prod.productKey,
              investmentOptionName: prod.investmentOptionName,
              investmentOptionFundCategory:
                prod.investmentOptionFundCategory === null ? 0 : prod.investmentOptionFundCategory,
              investmentOptionUnderlyingType: prod.investmentOptionUnderlyingType,
            },
          })),
          dataMax: data.marketProductsChartItems.map((prod) => ({
            y: prod.max,
            meta: {
              ...prod.productKey,
              investmentOptionName: prod.investmentOptionName,
              investmentOptionFundCategory:
                prod.investmentOptionFundCategory === null ? 0 : prod.investmentOptionFundCategory,
              investmentOptionUnderlyingType: prod.investmentOptionUnderlyingType,
            },
          })),
          dataMed: data.marketProductsChartItems.map((prod) => ({
            y: prod.med,
            meta: {
              ...prod.productKey,
              investmentOptionName: prod.investmentOptionName,
              investmentOptionFundCategory:
                prod.investmentOptionFundCategory === null ? 0 : prod.investmentOptionFundCategory,
              investmentOptionUnderlyingType: prod.investmentOptionUnderlyingType,
            },
          })),
        }
      } else {
        this.BEResponses[chart] = { productData: null, dataMin: [], dataMax: [], dataMed: [] }
      }
      this.isLoading[chart] = false
    },
    _isProductIncludedCallback(resp) {
      if (resp !== null) {
        this.showSearchCompatibilityAlert = resp.isSearchCompatibleWithProduct === false
        this.marketProductsTotalCount = resp.marketProductsTotalCount
        this.marketInvestmentOptionsTotalCount = resp.marketInvestmentOptionsTotalCount
      }
    },
    showProducts(params) {
      Object.assign(this.modalContent, params)
      this.modalContent.filter = ''
      this.$bvModal.show('marketComparation')
    },
    currentIndicatorText(category) {
      return this.indicesValues[category + 'Ind'].find((el) => el.value === this.indicators[category + 'Ind']).text
    },
    onClickTableRow(value) {
      this.openProductDetails(value)
    },
  },
  computed: {
    smartFilterEntry() {
      return this.ungroupedFilters.find((f) => f.filterKey === 'SmartFilter') || null
    },
    otherUngrouped() {
      return this.ungroupedFilters.filter((f) => f.filterKey !== 'SmartFilter')
    },
    smartFilterValue() {
      return this.filterValue('SmartFilter') || ''
    },
    // One chip per selected value (MultiSelect arrays split into individual chips), SmartFilter
    // excluded (it's shown as its own search box, not a chip, matching legacy). Simplified vs.
    // ActiveFilters.vue: no Perimeter/CompanyFilter/interval-bucket special-casing — those
    // filter_types aren't part of the Market Comparison preset and are rare in this context; they
    // fall back to a generic "key: raw value" label.
    filterChips() {
      return this.activeFilters.flatMap((entry) => {
        if (entry.key === 'SmartFilter') {
          return []
        }
        if (Array.isArray(entry.value)) {
          return entry.value.map((item, index) => ({
            id: entry.key + ':' + index,
            key: entry.key,
            item,
            text: this.filterLabel(entry.key) + ': ' + this.valueLabel(entry.key, item),
          }))
        }
        if (entry.value && typeof entry.value === 'object' && ('from' in entry.value || 'to' in entry.value)) {
          const from = entry.value.from === null || entry.value.from === undefined ? '' : entry.value.from
          const to = entry.value.to === null || entry.value.to === undefined ? '' : entry.value.to
          return [{ id: entry.key, key: entry.key, item: null, text: this.filterLabel(entry.key) + ': ' + from + ' - ' + to }]
        }
        return [
          {
            id: entry.key,
            key: entry.key,
            item: null,
            text: this.filterLabel(entry.key) + ': ' + this.valueLabel(entry.key, entry.value),
          },
        ]
      })
    },
    // structureType/issuerName arrive as query params from ProductDetails.vue's own pushToRoute
    // (already-loaded Characteristics/top-level DetailPageResponse data) — this page no longer fetches
    // its own copy via the deleted `/api/detail/product` (ProductDetailsEndpoint).
    isMop: function () {
      return this.$route.query.structureType === 'Mop' && this.investmentOptionName === null
    },
    issuerName: function () {
      return this.$route.query.issuerName
    },
    getProductComparisonMessageIcon() {
      return this.marketProductsTotalCount !== 0
        ? this.showSearchCompatibilityAlert
          ? { icon: 'fas fa-exclamation-triangle', class: 'icon-exclamation' }
          : { icon: 'fas fa-check', class: 'icon-check' }
        : { icon: 'fas fa-times', class: 'icon-times' }
    },
    getSidComparisonMessageIcon() {
      return this.marketInvestmentOptionsTotalCount !== 0
        ? this.showSearchCompatibilityAlert
          ? { icon: 'fas fa-exclamation-triangle', class: 'icon-exclamation' }
          : { icon: 'fas fa-check', class: 'icon-check' }
        : { icon: 'fas fa-times', class: 'icon-times' }
    },
    shownFieldsInModal() {
      let fields = [
        { key: 'issuerName', label: this.$t('search.resultTable.issuer'), sortable: true },
        { key: 'name', label: this.$t('common.product'), sortable: true },
      ]

      this.investmentOptionName !== null
        ? fields.push(
            { key: 'investmentOptionName', label: this.$t('common.option'), sortable: true },
            { key: 'investmentOptionUnderlyingType', label: this.$t('common.typology'), sortable: true },
            { key: 'investmentOptionFundCategory', label: this.$t('common.category'), sortable: true }
          )
        : null

      return fields
    },
    minMaxInModal() {
      if (['SRI', 'MRM'].includes(this.indicators[this.modalContent.chartCategory + 'Ind'])) {
        return round(this.modalContent.min, 0)
      }
      if (this.modalContent.chartCategory === 'other') {
        return rhpToYears(this.modalContent.min) + ' - ' + rhpToYears(this.modalContent.max)
      }
      if (
        this.modalContent.chartCategory === 'performance' &&
        this.indicators['performanceInd'] !== 'ExcessReturnOnVar'
      ) {
        return this.$n(this.modalContent.min, 'decimal') + ' - ' + this.$n(this.modalContent.max, 'decimal')
      }
      return this.$n(this.modalContent.min, 'percent') + ' - ' + this.$n(this.modalContent.max, 'percent')
    },
    setColumnsParent() {
      return this.isMop ? '12' : '6'
    },
    productsInModal: function () {
      let temp = this.BEResponses[this.modalContent.chartCategory]['data' + this.modalContent.valuesType].filter(
        (el) => round(el.y, 8) >= round(this.modalContent.min, 8)
      )
      if (!this.modalContent.isLastColumns) {
        temp = temp.filter((el) => round(el.y, 8) < round(this.modalContent.max, 8))
      }
      return temp.map((el) => {
        return {
          y: el.y,
          investmentOptionFundCategory:
            this.filterSelectValues.optionFundCategory[el.meta.investmentOptionFundCategory],
          investmentOptionName: el.meta.investmentOptionName,
          investmentOptionUnderlyingType:
            this.filterSelectValues.optionUnderlyingType[el.meta.investmentOptionUnderlyingType],
          issuerCode: el.meta.issuerCode,
          issuerName: el.meta.issuerName,
          name: el.meta.name,
          referenceDate: el.meta.referenceDate,
        }
      })
    },
    decodedProductName: function () {
      if (isBase64Url(this.productName)) {
        return base64UrlDecode(this.productName)
      } else {
        return this.productName
      }
    },
    decodedOptionName: function () {
      if (this.investmentOptionName !== null && isBase64Url(this.investmentOptionName)) {
        return base64UrlDecode(this.investmentOptionName)
      }
      return null
    },
    indicesValues: function () {
      const all = this.getMarketComparationIndices
      if (this.validIndicatorCodes === null) {
        return { ...all }
      }

      const filtered = {}
      Object.keys(all).forEach((category) => {
        filtered[category] = all[category].filter((option) => this.validIndicatorCodes.includes(option.value))
      })
      return filtered
    },

    marketComparationModalValue() {
      return {
        title: this.$t('common.product') + ': ' + this.decodedProductName,
        subtitle: this.decodedOptionName != null ? this.$t('common.option') + ': ' + this.decodedOptionName : '',
        subHeader: {
          number: this.productsInModal.length,
          label: this.$t('common.modal.elementsInRange'),
        },
        otherInfo: {
          label1: this.$t('common.indicators.' + this.modalContent.chartCategory + 'Ind'),
          label2: this.isMop ? 'Set: ' + this.$t('common.' + this.modalContent.valuesType) : '',
          label3: this.$t('common.modal.rangeFilter') + ' ' + this.minMaxInModal,
        },
        placeholder: this.$t('common.searchBarPlaceholder'),
        items: this.productsInModal,
        fields: this.shownFieldsInModal,
        labelDisplayClass: ['d-block'],
      }
    },
  },
  async mounted() {
    if (this.decodedProductName == null) {
      return this.$router.push('/search')
    }

    if (this.referenceDate != null && !isValidDate(this.referenceDate)) {
      return this.httpErrorsHandler('404')
    }

    // Awaited before the restore-from-localStorage validation below (and before the Promise.all
    // further down), so `indicesValues` is already filtered by asset class by the time anything
    // reads it — otherwise a saved selection from a previous asset class (or the very first
    // render) could restore/keep an indicator not valid here, same bug this whole fetch exists
    // to fix.
    const indicatorCodesResp = await this.get('/api/marketComparison/indicatorCodes')
    this.validIndicatorCodes = (indicatorCodesResp && indicatorCodesResp.codes) || []

    const saved = JSON.parse(localStorage.getItem(assetClassStorageKey('okv-market-comparation-indicators')))
    if (saved) {
      this.$_restoring = true
      Object.keys(this.indicators).forEach((key) => {
        const isValid = this.indicesValues[key] && this.indicesValues[key].some((o) => o.value === saved[key])
        if (isValid) {
          this.indicators[key] = saved[key]
        }
      })
      this.$_restoring = false
    }

    // Defends the (unlikely, but possible if the hardcoded defaults above ever drift from the
    // catalog) case where a currently-selected indicator isn't valid for this asset class either
    // — falls back to the first still-available option in that category rather than silently
    // keeping an indicator the BE will produce no data for. $_restoring guard as above: the
    // individual per-category watchers each fire their own `search()` call, which `rerunSearch()`
    // right after would otherwise duplicate.
    this.$_restoring = true
    Object.keys(this.indicators).forEach((key) => {
      const options = this.indicesValues[key]
      if (options && options.length > 0 && !options.some((o) => o.value === this.indicators[key])) {
        this.indicators[key] = options[0].value
      }
    })
    this.$_restoring = false

    const productKey = { issuerCode: this.issuer, name: this.decodedProductName, referenceDate: this.referenceDate }

    // No longer fetches its own product/structure copy here — issuerName/structureType (isMop) arrive
    // as query params from ProductDetails.vue's pushToRoute instead (see isMop/issuerName above).
    const [catalog, presetEntries] = await Promise.all([
      this.get('/api/filters-catalog'),
      this.post('/api/marketComparison/presetFilters', productKey),
    ])

    this.filterGroups = (catalog && catalog.groups) || []
    this.ungroupedFilters = (catalog && catalog.ungrouped) || []
    this.activeFilters = presetEntries || []

    this.rerunSearch()
  },
}
</script>

<style lang="scss" scoped>
.productInfo span {
  display: inline-block;
  vertical-align: top;
}

/* Own copy, same values as IndicatorsHistory.vue's own scoped copy — both pages built their own
   rather than sharing one, so this follows suit. */
.detailsTemplateProductName {
  font-size: 1.3rem;
}
.detailsTemplateIssuer {
  font-size: 1rem;
}

.detailsTemplateOptionName {
  font-size: 1.3rem;
  font-weight: 500;
}

/deep/ .chartHeader {
  font-size: 1.3rem;
  font-weight: bold;
}

/deep/.alert-msg {
  color: red;
  display: block;
  font-size: 0.8rem;
  margin-top: 0.3rem;
}

/deep/ .icon-check {
  color: #008000;
  font-size: 1rem;
}

/deep/ .icon-times {
  color: #ff0000;
  font-size: 1rem;
}

/deep/ .icon-exclamation {
  color: #ff0000;
  font-size: 1rem;
  margin: 4px 4px;
  left: -15px;
  top: 3px;
  position: absolute;
}
</style>
